import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GoogleMap,
  DirectionsRenderer,
  useJsApiLoader,
  Marker,
} from "@react-google-maps/api";

const LIBRARIES: "places"[] = ["places"];

interface RouteMapProps {
  origin: { lat: number; lng: number } | null;
  destination: { lat: number; lng: number } | null;
  height?: string;
}

const RouteMap: React.FC<RouteMapProps> = ({
  origin,
  destination,
  height = "500px",
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.APP_MAP_API_KEY || "",
    libraries: LIBRARIES,
  });

  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const [distance, setDistance] = useState<string | null>(null);
  const [duration, setDuration] = useState<string | null>(null);

  const center = origin || destination || { lat: 27.7172, lng: 85.324 }; // Default to Kathmandu

  useEffect(() => {
    if (isLoaded && origin && destination) {
      const directionsService = new google.maps.DirectionsService();

      directionsService.route(
        {
          origin: origin,
          destination: destination,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            setDirections(result);
            setError(null);

            // Extract distance and duration
            const leg = result.routes[0]?.legs[0];
            if (leg) {
              setDistance(leg.distance?.text || null);
              setDuration(leg.duration?.text || null);
            }
          } else {
            console.error(`Directions request failed due to ${status}`);
            setError(`Could not fetch directions: ${status}`);
          }
        },
      );
    }
  }, [isLoaded, origin, destination]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <div style={{ width: "100%", height, position: "relative" }}>
      {/* Distance Overlay */}
      {distance && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            zIndex: 10,
            background: "white",
            padding: "8px 12px",
            borderRadius: "8px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
            Distance: {distance}
          </div>
          {duration && (
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Est. Time: {duration}
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "8px",
            background: "#fee2e2",
            color: "#b91c1c",
            borderRadius: "4px",
            marginBottom: "8px",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={13}
        onLoad={onMapLoad}
      >
        {/* Render Directions if available */}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: false, // Let it show default markers A and B
              polylineOptions: {
                strokeColor: "#4f46e5",
                strokeWeight: 5,
              },
            }}
          />
        )}

        {/* Fallback markers if directions fail or only one point exists */}
        {!directions && origin && <Marker position={origin} label="Store" />}
        {!directions && destination && (
          <Marker position={destination} label="Customer" />
        )}
      </GoogleMap>
    </div>
  );
};

export default RouteMap;
